/**
 * React hook that applies dynamic transforms to step config data.
 *
 * Synchronous transforms (swap_assets, template_url) are applied eagerly
 * during render so the first paint already has correct images/URLs.
 * Async transforms (filter_options) are deferred to useEffect.
 *
 * NX-9279
 */
import { useEffect, useMemo, useState } from "react";
import {
  applyTransforms,
  applySyncTransforms,
  deepInterpolate,
  parseTransforms,
} from "@survey-kit/transforms";

const stripTransforms = (d: Record<string, unknown>): Record<string, unknown> => {
  const { transforms: _, ...rest } = d;
  return rest;
};

const hasAsyncTransforms = (transforms: ReadonlyArray<{ type: string }>): boolean =>
  transforms.some((t) => t.type === "filter_options");

/**
 * Given the step's `data` payload and all collected answers, applies any
 * `transforms` declared in the config and returns the result.
 *
 * Sync transforms are applied immediately; async filter_options resolves
 * after the first paint but typically within the same frame.
 */
export const useTransformedData = (
  data: Record<string, unknown>,
  answers: Record<string, unknown>,
): Record<string, unknown> => {
  const transforms = useMemo(() => parseTransforms(data), [data]);
  const cleanData = useMemo(() => stripTransforms(data), [data]);

  // Apply sync transforms (swap_assets, template_url) eagerly during render.
  const syncResult = useMemo(
    () => (transforms.length > 0 ? applySyncTransforms(data, transforms, answers) : cleanData),
    [data, transforms, answers, cleanData],
  );

  const needsAsync = hasAsyncTransforms(transforms);
  const [asyncResult, setAsyncResult] = useState<Record<string, unknown>>(syncResult);

  // Reset async result whenever sync result changes — only when async transforms exist.
  const [prevSyncResult, setPrevSyncResult] = useState(syncResult);
  if (needsAsync && prevSyncResult !== syncResult) {
    setPrevSyncResult(syncResult);
    setAsyncResult(syncResult);
  }

  useEffect(() => {
    if (!needsAsync) return;

    let cancelled = false;

    void applyTransforms(data, transforms, answers)
      .then((result) => {
        if (!cancelled) setAsyncResult(result);
      })
      .catch(() => {
        if (!cancelled) setAsyncResult(syncResult);
      });

    return () => {
      cancelled = true;
    };
  }, [data, transforms, answers, syncResult, needsAsync]);

  // Final pass: auto-interpolate any remaining {{key}} tokens that weren't
  // covered by explicit transforms. Safe because {{…}} is never a valid
  // literal value in step config.
  const base = transforms.length === 0 ? cleanData : !needsAsync ? syncResult : asyncResult;
  return useMemo(() => deepInterpolate(base, answers) as Record<string, unknown>, [base, answers]);
};
